import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de entornos.
import { environment } from '../../../../environments/environment';

// Importaciones de servicios.
import { LogService } from '../../../services/log.service';
import { MeService } from '../../../services/me.service';

// Importaciones de interfaces.
import { IEmployee } from '../../../interfaces/employee.interface';

// Importaciones de modelos.
import { Employee } from '../../../models/employee.model';
import { Log } from '../../../models/log.model';

/**
 * Componente para la configuración del perfil del usuario.
 * Permite gestionar y actualizar la información personal del perfil de un empleado.
 */
@Component({
  selector: 'app-profile-settings',
  templateUrl: './profile-settings.component.html',
  standalone: false,
})
export class ProfileSettingsComponent implements OnInit {
  /**
   * Indica si se está cargando el perfil del empleado.
   * Se usa para controlar el estado de carga en la vista del perfil (por ejemplo, mostrar un "loading spinner").
   */
  loadingProfile: boolean = false;

  /**
   * Indica si se está guardando el perfil del empleado.
   * Se usa para controlar el estado de carga cuando se están guardando los cambios del perfil.
   */
  loadingSaveProfile: boolean = false;

  /**
   * Array que almacena los mensajes de error generados al intentar guardar el perfil del usuario.
   * Cada error se agrega como un string, y se utiliza para mostrar los errores de validación o de servidor en la vista.
   */
  errors: string[] = [];

  /**
   * Formulario reactivo con los controles para cada campo del perfil del empleado.
   * Incluye validaciones para los campos de nombre de usuario, email y contraseña.
   */
  form: FormGroup = new FormGroup({
    username: new FormControl('', Validators.required),
    email: new FormControl('', [
      Validators.required,
      Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'),
    ]),
    displayName: new FormControl(),
  });

  /**
   * Getter que devuelve el control del formulario para el campo "username" (nombre de usuario).
   * Permite acceder a dicho control desde la vista para realizar validaciones, modificaciones o mostrar su valor.
   */
  get formUsername(): AbstractControl | null {
    return this.form.get('username');
  }

  /**
   * Getter que devuelve el control del formulario para el campo "email" (correo electrónico del usuario).
   * Permite acceder a dicho control desde la vista para realizar validaciones, modificaciones o mostrar su valor.
   */
  get formEmail(): AbstractControl | null {
    return this.form.get('email');
  }

  /**
   * Getter que devuelve el control del formulario para el campo "displayName" (nombre a mostrar del usuario).
   * Permite acceder a dicho control desde la vista para realizar validaciones, modificaciones o mostrar su valor.
   */
  get formDisplayName(): AbstractControl | null {
    return this.form.get('displayName');
  }

  /**
   * Constructor del componente.
   *
   * @param logService - Servicio encargado de la gestión de los registros.
   * @param meService - Servicio encargado de la gestión de datos relacionados con el usuario actual.
   * @param snackBar - Servicio de Angular Material utilizado para mostrar mensajes emergentes en la interfaz de usuario.
   */
  constructor(
    private logService: LogService,
    private meService: MeService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método del ciclo de vida `OnInit` de Angular.
   * Se ejecuta una vez que el componente ha sido inicializado.
   *
   * Aquí es común realizar tareas de initialization, como cargar datos o realizar peticiones al backend.
   */
  ngOnInit(): void {
    this.getProfile();
  }

  /**
   * Método asíncrono que obtiene el perfil del usuario actual.
   * Realiza una petición al servicio correspondiente para recuperar la información del perfil.
   *
   * En caso de éxito, los datos se asignan a las propiedades correspondientes del componente.
   * Si ocurre algún error, se maneja de acuerdo a la lógica definida (ej. mostrar un mensaje de error).
   */
  async getProfile(): Promise<void> {
    this.loadingProfile = true;

    try {
      const employeeResponse: IEmployee = await this.meService.get();
      this.form.patchValue(employeeResponse);
    } finally {
      this.loadingProfile = false;
    }
  }

  /**
   * Método asíncrono que guarda los cambios realizados en el perfil del usuario.
   * Realiza una petición al servicio correspondiente para guardar los datos actualizados del perfil.
   *
   * En caso de éxito, el perfil se guarda correctamente. En caso de error, se maneja de acuerdo a la lógica definida (ej. mostrar un mensaje de error).
   */
  async saveProfile(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    this.loadingSaveProfile = true;
    this.errors = [];

    try {
      const meResponse: string = await this.meService.update(
        new Employee({
          displayName: this.formDisplayName?.value,
          username: this.formUsername?.value,
          email: this.formEmail?.value,
        })
      );

      // Añadir un registro de edición del perfil.
      try {
        await this.logService.create(
          new Log({
            description: 'Ha editado el perfil.',
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de edición del perfil.'
          );
        }
      }

      this.snackBar.open(meResponse, '', {
        duration: 5000,
      });
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loadingSaveProfile = false;
  }
}
